import React, { FunctionComponent } from 'react';

import clsx from 'clsx';

import { getLink, KnownRoutes } from '@utils/route';

import { Link } from '@components/common/Link/Link';
import { Spinner } from '@components/common/Spinner/Spinner';

import styles from './Button.module.scss';

type ClassKey = 'content' | 'icon';
export type ButtonVariant = 'green' | 'greenLight' | 'white' | 'whiteRed';
type ButtonSize = 'small' | 'normal' | 'medium' | 'large';

export type ButtonProps = {
    variant: ButtonVariant;
    link?: string | KnownRoutes;
    icon?: SvgIcon;
    size?: ButtonSize;
    target?: string;
    isLoading?: boolean;
    isFullWith?: boolean;
    classes?: Classes<ClassKey>;
    rel?: string;
    'data-testid'?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;
export const Button: FunctionComponent<ButtonProps> = (props) => {
    const {
        variant,
        link,
        size,
        icon: ButtonIcon,
        disabled,
        isLoading,
        isFullWith,
        className,
        children,
        target,
        onClick,
        classes,
        rel,
        'data-testid': dataTestId,
        ...otherProps
    } = props;

    const hasChildren = children !== undefined;
    const hasIcon = ButtonIcon !== undefined;

    const innerContent = (
        <>
            {ButtonIcon !== undefined && (
                <ButtonIcon
                    className={clsx(styles.icon, isLoading && styles.contentHidden, classes?.icon)}
                />
            )}

            <span className={clsx(classes?.content, isLoading && styles.contentHidden)}>
                {children}
            </span>

            {isLoading !== undefined && (
                <span className={clsx(styles.loader, !isLoading && styles.loaderHidden)}>
                    <Spinner />
                </span>
            )}
        </>
    );

    const innerClass = clsx(
        styles.root,
        styles[`${variant}Variant`],
        styles[`${size}Size`],
        isFullWith && styles.fullWidth,
        hasIcon && !hasChildren && styles.onlyIcon,
        className,
    );

    return link !== undefined ? (
        <Link
            route={getLink(link)}
            target={target}
            className={innerClass}
            rel={rel}
            onClick={onClick}
            data-testid={dataTestId}
        >
            {innerContent}
        </Link>
    ) : (
        <button
            className={innerClass}
            type="button"
            disabled={disabled || isLoading}
            onClick={onClick}
            data-testid={dataTestId}
            {...otherProps}
        >
            {innerContent}
        </button>
    );
};
